import type { Family, FamilyAdult } from "@teamna/shared";
import { useState } from "react";
import { CloseIcon, Field } from "../../components/index.ts";
import { phoneText } from "./model.ts";
import type { FamilyActions } from "./types.ts";

/**
 * The editors of one family, on the Settings tab of a parent's page: an adult (name and number,
 * which the owner can change only until the person signs in), and the small remove button.
 */
export function AdultEditor({
  family,
  adult,
  actions,
  onError,
}: {
  family: Family;
  adult: FamilyAdult;
  actions: FamilyActions;
  onError: (error: unknown) => void;
}) {
  const [name, setName] = useState(adult.name);
  const [phone, setPhone] = useState(adult.phoneE164 ? phoneText(adult.phoneE164) : "");

  function save(patch: { name?: string; phone?: string }) {
    void actions.updateAdult(family.id, adult.userId, patch).catch((error: unknown) => {
      setName(adult.name);
      setPhone(adult.phoneE164 ? phoneText(adult.phoneE164) : "");
      onError(error);
    });
  }

  if (!adult.editable) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-surface px-3 py-2.5">
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-ink" dir="auto">
            {adult.name}
          </span>
          <span className="text-[13px] text-ink-2">{phoneText(adult.phoneE164)}. In the app: only they change their name and number.</span>
        </span>
        {!adult.primary ? (
          <RemoveButton label={`Remove ${adult.name}`} onClick={() => void actions.removeAdult(family.id, adult.userId).catch(onError)} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field
        size="compact"
        label={adult.primary ? "Name" : "Second adult"}
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => {
          if (name.trim() && name.trim() !== adult.name) save({ name: name.trim() });
        }}
        className="min-w-[160px]"
        dir="auto"
      />
      <Field
        size="compact"
        label="Mobile number"
        type="tel"
        inputMode="tel"
        placeholder="Add a number"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
        onBlur={() => {
          const before = adult.phoneE164 ? phoneText(adult.phoneE164) : "";
          if (phone.trim() !== before) save({ phone: phone.trim() });
        }}
        className="min-w-[150px]"
      />
      {!adult.primary ? (
        <RemoveButton label={`Remove ${adult.name || "this adult"}`} onClick={() => void actions.removeAdult(family.id, adult.userId).catch(onError)} />
      ) : null}
    </div>
  );
}

export function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex size-10 shrink-0 items-center justify-center rounded-lg text-ink-2 hover:bg-sunken hover:text-ink"
    >
      <CloseIcon size={16} />
    </button>
  );
}

export function toAge(value: string): number | null {
  const number = Number.parseInt(value.trim(), 10);
  return Number.isFinite(number) && number >= 0 && number < 100 ? number : null;
}
